import * as React from "react";
import { isConsentBannerEnabled } from "../config/analytics";

const STORAGE_KEY = "analytics_consent";
const VISITOR_KEY = "analytics_visitor_id";
const SESSION_KEY = "analytics_session_id";

export type ConsentValue = "granted" | "denied";

interface ConsentContextValue {
  consent: ConsentValue | null;
  grant: () => void;
  deny: () => void;
  revoke: () => void;
  openSettings: () => void;
  closeSettings: () => void;
  settingsOpen: boolean;
}

const ConsentContext = React.createContext<ConsentContextValue | null>(null);

export function useConsent(): ConsentContextValue {
  const ctx = React.useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used inside <ConsentProvider>");
  }
  return ctx;
}

export function hasConsent(): boolean {
  // Extended consent only matters in "full" mode. In "off" or "anonymous" mode
  // we treat consent as absent regardless of any leftover localStorage.
  if (!isConsentBannerEnabled()) return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "granted";
  } catch {
    return false;
  }
}

function clearTrackingStorage() {
  try {
    window.localStorage.removeItem(VISITOR_KEY);
  } catch {}
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {}
}

function clearAllConsentStorage() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {}
  clearTrackingStorage();
}

export const ConsentProvider = React.memo<React.PropsWithChildren<{}>>(
  function ConsentProvider({ children }) {
    const [consent, setConsentState] = React.useState<ConsentValue | null>(
      () => {
        if (!isConsentBannerEnabled()) return null;
        try {
          const v = window.localStorage.getItem(STORAGE_KEY);
          if (v === "granted" || v === "denied") return v;
        } catch {}
        return null;
      }
    );
    const [settingsOpen, setSettingsOpen] = React.useState(false);

    // When the banner is disabled, scrub any stale entries that may exist
    // from a previous run in "full" mode. This guarantees that switching the
    // mode back to "anonymous" or "off" leaves no PII residue in the browser.
    // Läuft einmal pro Seitenaufruf — nach einem Moduswechsel im Admin also
    // beim nächsten Reload im Browser des Besuchers.
    React.useEffect(() => {
      if (!isConsentBannerEnabled()) {
        clearAllConsentStorage();
      }
    }, []);

    const grant = React.useCallback(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, "granted");
      } catch {}
      setConsentState("granted");
      setSettingsOpen(false);
    }, []);

    const deny = React.useCallback(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, "denied");
      } catch {}
      clearTrackingStorage();
      setConsentState("denied");
      setSettingsOpen(false);
    }, []);

    const revoke = React.useCallback(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, "denied");
      } catch {}
      clearTrackingStorage();
      setConsentState("denied");
    }, []);

    const openSettings = React.useCallback(() => setSettingsOpen(true), []);
    const closeSettings = React.useCallback(() => setSettingsOpen(false), []);

    const value = React.useMemo<ConsentContextValue>(
      () => ({
        consent,
        grant,
        deny,
        revoke,
        openSettings,
        closeSettings,
        settingsOpen,
      }),
      [consent, grant, deny, revoke, openSettings, closeSettings, settingsOpen]
    );

    return (
      <ConsentContext.Provider value={value}>
        {children}
      </ConsentContext.Provider>
    );
  }
);
